/**
 * PARÇA 437 — Public privacy policy page.
 *
 * Hosted at https://event.dayivo.com/privacy — REQUIRED for App Store +
 * Play Store submission. The full long-form policy lives in
 * `event/legal/privacy_policy_en.md`; this is the public-facing version
 * that lawyers will eventually review and edit.
 *
 * Until lawyer review is complete, the page shows a draft banner so
 * visitors understand the status. Once cleared by counsel, remove the
 * banner before final store submission.
 */
import React from 'react';

export function PrivacyPage(): React.JSX.Element {
  return (
    <main className="mx-auto max-w-3xl px-6 py-10 text-gray-900 dark:text-gray-100">
      <header className="mb-8">
        <h1 className="text-3xl font-semibold">Privacy Policy</h1>
        <p className="mt-2 text-sm text-gray-500">Last updated: 2026-05-31 (Draft pending lawyer review)</p>
      </header>

      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
        <strong>Draft notice:</strong> This page is a developer draft prepared with PIPEDA (Canada) and Quebec Law 25 in mind. The final version will be reviewed by a Canadian privacy lawyer before public launch.
      </div>

      <section className="prose prose-sm mt-8 dark:prose-invert">
        <h2>1. Who we are</h2>
        <p>
          Nightlife is operated by Dayivo (the &quot;we&quot;, &quot;us&quot;, &quot;our&quot;). Privacy questions: <a href="mailto:privacy@event.dayivo.com">privacy@event.dayivo.com</a>.
        </p>

        <h2>2. What we collect</h2>
        <p>We collect only what we need to run the app:</p>
        <ul>
          <li><strong>You give us directly:</strong> email, username + display name, birth date (for age verification), city, music genre preferences, profile photo (optional), bio (optional), posts/stories/comments/reactions/direct messages you send.</li>
          <li><strong>Automatically:</strong> approximate location at the <strong>city level only</strong> (never exact GPS coordinates), device identifier (for push notifications), in-app event analytics, IP address (for security), crash reports (no personal data).</li>
          <li><strong>From Apple/Google sign-in:</strong> only the name + email you choose to share.</li>
          <li><strong>From Spotify (DJ track previews):</strong> public artist info only.</li>
        </ul>

        <h2>3. Why</h2>
        <p>Run the app, deliver realtime features, personalise recommendations, deliver push notifications, security/abuse prevention, legal compliance.</p>
        <p><strong>We do NOT use your data for advertising, profiling for credit decisions, or any purpose outside what you signed up for. We do not sell your data.</strong></p>

        <h2>4. Who we share it with</h2>
        <p>Only when you ask us to (e.g. posting a public story), when a service provider helps us run the infrastructure (under contracts mirroring our protections), or when the law requires it.</p>

        <h2>5. Where it lives</h2>
        <ul>
          <li>Primary data: PostgreSQL on a server located in <strong>Beauharnois, Québec, Canada</strong> (OVH SBG).</li>
          <li>Realtime: Redis (same region).</li>
          <li>Crash reports: Sentry (anonymised).</li>
          <li>Push notification routing: Expo (USA).</li>
        </ul>

        <h2>6. How long</h2>
        <ul>
          <li>Account active: indefinitely.</li>
          <li>Account deleted: soft-deleted for 30 days (recovery window), then hard-deleted.</li>
          <li>Stories: 24 hours.</li>
          <li>Analytics: 365 days then aggregated/anonymised.</li>
        </ul>

        <h2>7. Your rights (PIPEDA + Quebec Law 25)</h2>
        <ul>
          <li><strong>Access</strong> all data we hold (Settings → Privacy → Export My Data).</li>
          <li><strong>Correct</strong> inaccurate data (Profile → Edit).</li>
          <li><strong>Delete</strong> your account (Settings → Privacy → Delete Account).</li>
          <li><strong>Withdraw consent</strong> for optional processing (Settings → Notifications).</li>
          <li><strong>Complain</strong> to the <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer">Office of the Privacy Commissioner of Canada</a> or the <a href="https://www.cai.gouv.qc.ca" target="_blank" rel="noopener noreferrer">Commission d&apos;accès à l&apos;information du Québec</a>.</li>
        </ul>
        <p>Response time: 30 days maximum.</p>

        <h2>8. Security</h2>
        <p>TLS 1.2+, bcrypt password hashing, optional two-factor authentication, active-session list visible to you, rate limiting + audit logs to detect abuse. Breach notification within 72 hours per PIPEDA / Law 25.</p>

        <h2>9. Children</h2>
        <p>The app is <strong>17+</strong>. We do not knowingly collect data from anyone under 17.</p>

        <h2>10. Changes</h2>
        <p>Material changes announced in the app at least 30 days before taking effect.</p>

        <h2>11. Contact</h2>
        <p>
          Privacy Officer: <a href="mailto:privacy@event.dayivo.com">privacy@event.dayivo.com</a><br />
          Support: <a href="mailto:support@event.dayivo.com">support@event.dayivo.com</a>
        </p>

        <h2>Quebec Law 25 specific</h2>
        <p>We make no automated decisions about you. You can refuse profiling for personalised recommendations in Settings. Cross-border data transfer: data stays in Canada except anonymised crash reports — by using the app you consent to this.</p>
      </section>

      <footer className="mt-12 border-t border-gray-200 pt-6 text-sm text-gray-500 dark:border-gray-700">
        <a href="/" className="text-blue-600 hover:underline dark:text-blue-400">Back to home</a>
        <span className="mx-2">·</span>
        <a href="/terms" className="text-blue-600 hover:underline dark:text-blue-400">Terms of Service</a>
        <span className="mx-2">·</span>
        <a href="/support" className="text-blue-600 hover:underline dark:text-blue-400">Support</a>
      </footer>
    </main>
  );
}
